export interface CanonicalSecurityEntry {
  id: string;
  securityType?: string | null;
  description?: string | null;
  propertyAddress?: string | null;
  titleReference?: string | null;
  vehicleRegistrationNumber?: string | null;
  equipmentMake?: string | null;
  equipmentModel?: string | null;
  equipmentSerialNumber?: string | null;
  equipmentAssetTag?: string | null;
  financialAssetDescription?: string | null;
  financialAssetType?: string | null;
  financialAssetInstitutionName?: string | null;
}

export type CanonicalSecurityDraft = Omit<CanonicalSecurityEntry, "id">;

function prefixed(prefix: string, value: string | null): string | null {
  return value ? `${prefix}:${value}` : null;
}

function normalize(value?: string | null): string | null {
  if (!value) return null;
  const cleaned = value.trim().replace(/\s+/g, " ").toUpperCase();
  return cleaned.length > 0 ? cleaned : null;
}

function joinKeyParts(...parts: Array<string | null>): string | null {
  const normalized = parts.filter((part): part is string => Boolean(part));
  return normalized.length > 0 ? normalized.join("|") : null;
}

export function getCanonicalSecurityGroupKey<T extends CanonicalSecurityEntry>(security: T): string {
  const description = normalize(security.description);
  const propertyAddress = normalize(security.propertyAddress);
  const titleReference = normalize(security.titleReference);
  const vehicleRegistration = normalize(security.vehicleRegistrationNumber);
  const equipmentSerial = normalize(security.equipmentSerialNumber);
  const equipmentAssetTag = normalize(security.equipmentAssetTag);
  const equipmentName = joinKeyParts(normalize(security.equipmentMake), normalize(security.equipmentModel));
  const financialAsset = joinKeyParts(
    normalize(security.financialAssetDescription),
    normalize(security.financialAssetType),
    normalize(security.financialAssetInstitutionName),
  );

  switch (security.securityType) {
    case "residential_property":
    case "commercial_property":
      return (
        joinKeyParts(prefixed("PROPERTY", propertyAddress), prefixed("TITLE", titleReference)) ||
        prefixed("TITLE", titleReference) ||
        prefixed("PROPERTY", propertyAddress) ||
        (propertyAddress ? joinKeyParts(prefixed("PROPERTY", propertyAddress), description) : null) ||
        `ID:${security.id}`
      );
    case "vehicle":
      return joinKeyParts(prefixed("VEHICLE", vehicleRegistration), description) || `ID:${security.id}`;
    case "equipment":
      return (
        joinKeyParts(prefixed("EQUIPMENT", equipmentSerial), prefixed("TAG", equipmentAssetTag), equipmentName, description) ||
        `ID:${security.id}`
      );
    case "financial_asset":
      return joinKeyParts(prefixed("FINANCIAL", financialAsset), description) || `ID:${security.id}`;
    default:
      return description ? `DESC:${description}` : `ID:${security.id}`;
  }
}

export function getCanonicalSecurityDraftKey(security: CanonicalSecurityDraft): string | null {
  const key = getCanonicalSecurityGroupKey({
    id: "__draft__",
    ...security,
  });

  return key.startsWith("ID:") ? null : key;
}
